import { jsPDF } from 'jspdf';

/**
 * Servicio profesional para la generación del Brochure Comercial y Dossier Ejecutivo
 * de "MiA Cartera PH" en formato PDF vectorial de alta calidad, listo para imprimir,
 * descargar o presentar ante Administradores y Consejos de Copropiedad.
 */
export class CarteraPhBrochurePdfService {
  /**
   * Genera el documento PDF formal del Brochure Comercial (3 páginas balanceadas)
   */
  public static generateBrochurePdf(): jsPDF {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'letter', // 215.9 x 279.4 mm
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;
    const contentWidth = pageWidth - margin * 2; // 187.9 mm

    // Paleta cromática corporativa de alto impacto
    const primaryNavy: [number, number, number] = [15, 23, 42]; // Slate 900
    const emeraldAccent: [number, number, number] = [5, 150, 105]; // Emerald 600
    const darkEmerald: [number, number, number] = [6, 95, 70]; // Emerald 800
    const lightEmeraldBg: [number, number, number] = [236, 253, 245]; // Emerald 50
    const textDark: [number, number, number] = [30, 41, 59]; // Slate 800
    const textMuted: [number, number, number] = [100, 116, 139]; // Slate 500
    const lightBg: [number, number, number] = [248, 250, 252]; // Slate 50
    const lightGrayBg: [number, number, number] = [241, 245, 249]; // Slate 100
    const cardBorder: [number, number, number] = [203, 213, 225]; // Slate 300
    const emeraldMedium: [number, number, number] = [16, 185, 129]; // Emerald 500

    // Encabezado institucional por página
    const renderPageHeader = (pageNum: number, totalPages: number, pageTag: string) => {
      // Franja de acento superior
      doc.setFillColor(...primaryNavy);
      doc.rect(0, 0, pageWidth, 5, 'F');
      doc.setFillColor(...emeraldAccent);
      doc.rect(0, 5, pageWidth, 1.5, 'F');

      const yPos = margin + 1;

      // Logotipo / Marca
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(...primaryNavy);
      doc.text('MiA LEGAL PH', margin, yPos);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...textMuted);
      doc.text('Plataforma LegalTech de Recuperación de Cartera | Ley 675 de 2001 & Ley 2300 de 2023', margin, yPos + 4.5);

      // Contacto comercial a la derecha
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(...darkEmerald);
      doc.text('WhatsApp: +57 350 767 9981', pageWidth - margin, yPos, { align: 'right' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(...textMuted);
      doc.text('contacto@miasistentelegalia.com | www.miasistentelegalia.com', pageWidth - margin, yPos + 4.5, { align: 'right' });

      const lineY = yPos + 7.5;
      doc.setDrawColor(...cardBorder);
      doc.setLineWidth(0.4);
      doc.line(margin, lineY, pageWidth - margin, lineY);

      // Pie de página institucional
      const footerY = pageHeight - 8;
      doc.setDrawColor(...cardBorder);
      doc.setLineWidth(0.3);
      doc.line(margin, footerY - 2, pageWidth - margin, footerY - 2);

      doc.setFontSize(7);
      doc.setTextColor(...textMuted);
      doc.text(
        'MiA Cartera PH © 2026 - Dossier Comercial Confidencial para Administraciones y Consejos de Administración.',
        margin,
        footerY + 1.5
      );
      doc.text(`Página ${pageNum} de ${totalPages}`, pageWidth - margin, footerY + 1.5, { align: 'right' });

      return lineY + 5;
    };

    // Helper para dibujar badge circular de éxito (círculo verde esmeralda con checkmark blanco nítido)
    const drawSuccessBadge = (cx: number, cy: number, radius = 1.8) => {
      doc.setFillColor(16, 185, 129); // Emerald 500
      doc.circle(cx, cy, radius, 'F');
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.38);
      const r = radius;
      doc.line(cx - r * 0.45, cy + r * 0.05, cx - r * 0.12, cy + r * 0.45);
      doc.line(cx - r * 0.12, cy + r * 0.45, cx + r * 0.48, cy - r * 0.38);
    };

    // Helper para dibujar badge circular de advertencia/cobro tradicional (círculo rojo con cruz blanca nítida)
    const drawWarningBadge = (cx: number, cy: number, radius = 1.8) => {
      doc.setFillColor(239, 68, 68); // Red 500
      doc.circle(cx, cy, radius, 'F');
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.38);
      const d = radius * 0.42;
      doc.line(cx - d, cy - d, cx + d, cy + d);
      doc.line(cx - d, cy + d, cx + d, cy - d);
    };

    // Helper para bullets en las tarjetas de planes (círculo esmeralda con checkmark blanco nítido)
    const drawPlanBullet = (cx: number, cy: number, isBest = false) => {
      doc.setFillColor(...(isBest ? emeraldAccent : emeraldMedium));
      doc.circle(cx, cy, 1.4, 'F');
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.32);
      doc.line(cx - 0.6, cy + 0.05, cx - 0.15, cy + 0.55);
      doc.line(cx - 0.15, cy + 0.55, cx + 0.65, cy - 0.48);
    };

    // ==========================================
    // ── PÁGINA 1: PORTADA & DIAGNÓSTICO EJECUTIVO
    // ==========================================
    let y = renderPageHeader(1, 3, 'Presentación General');

    // 1. Banner Hero Portada
    const heroH = 46;
    doc.setFillColor(...lightBg);
    doc.roundedRect(margin, y, contentWidth, heroH, 3, 3, 'FD');
    doc.setDrawColor(...emeraldAccent);
    doc.setLineWidth(0.8);
    doc.roundedRect(margin, y, contentWidth, heroH, 3, 3, 'D');

    // Badge superior
    doc.setFillColor(...emeraldAccent);
    doc.roundedRect(margin + 6, y + 4.5, 68, 5.5, 1, 1, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(255, 255, 255);
    doc.text('DOSSIER COMERCIAL PARA CONJUNTOS Y PH', margin + 8, y + 8.5);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.setTextColor(...primaryNavy);
    doc.text('Recupere la Cartera de su Copropiedad sin Intermediarios', margin + 6, y + 18);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(...darkEmerald);
    doc.text('Solución LegalTech con Botón PSE en WhatsApp, Título Ejecutivo y Blindaje Ley 2300', margin + 6, y + 23.5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...textDark);
    const heroDesc =
      'MiA Cartera PH es la plataforma especializada para Administradores de Propiedad Horizontal y Consejos en Colombia. Integramos recaudo por WhatsApp con botón PSE directo a la cuenta del conjunto, liquidación de intereses moratorios a la tasa legal vigente de la Superfinanciera (Ley 675) y blindaje algorítmico contra sanciones de la Ley 2300 de 2023 ("Dejen de Fregar").';
    doc.text(doc.splitTextToSize(heroDesc, contentWidth - 12), margin + 6, y + 29.5);

    y += heroH + 5;

    // 2. Cuatro Tarjetas de Métricas de Impacto
    const cardW = (contentWidth - 6) / 4;
    const cardH = 23;
    const metrics = [
      { num: '+45%', label: 'Velocidad de Recaudo', desc: 'En los primeros 15 días' },
      { num: '30 Seg', label: 'Pago Inmediato PSE', desc: 'Directo al banco del PH' },
      { num: '1 Clic', label: 'Título Ejecutivo', desc: 'Art. 48 Ley 675 en PDF' },
      { num: '100%', label: 'Blindaje Ley 2300', desc: 'Cero sanciones de la SIC' },
    ];

    metrics.forEach((m, idx) => {
      const cardX = margin + idx * (cardW + 2);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(cardX, y, cardW, cardH, 2, 2, 'FD');
      doc.setDrawColor(...cardBorder);
      doc.setLineWidth(0.4);
      doc.roundedRect(cardX, y, cardW, cardH, 2, 2, 'D');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(...darkEmerald);
      doc.text(m.num, cardX + cardW / 2, y + 8, { align: 'center' });

      doc.setFontSize(7.5);
      doc.setTextColor(...primaryNavy);
      doc.text(m.label, cardX + cardW / 2, y + 13.5, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.8);
      doc.setTextColor(...textMuted);
      doc.text(m.desc, cardX + cardW / 2, y + 18, { align: 'center' });
    });

    y += cardH + 6;

    // 3. Sección Diagnóstico: La Realidad de la Cartera PH
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...primaryNavy);
    doc.text('1. El Desafío Financiero en los Conjuntos Residenciales', margin, y);
    y += 4;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...textDark);
    const diagText =
      'En Colombia, entre el 12% y el 28% del presupuesto anual de una copropiedad se encuentra congelado en cuotas morosas. Los métodos habituales (cartas impresas bajo puerta, llamadas telefónicas o mensajes personales) desgastan al administrador, no brindan inmediatez para pagar y exponen a la administración a sanciones millonarias de la SIC (Ley 2300 de 2023).';
    doc.text(doc.splitTextToSize(diagText, contentWidth), margin, y);
    y += 14;

    // 4. Cuadro Comparativo: Cobro Tradicional vs. MiA Cartera PH
    const col1W = 38;
    const col2W = 74;
    const col3W = contentWidth - col1W - col2W; // 187.9 - 38 - 74 = 75.9 mm

    // Cabecera de la tabla
    const tableHeaderH = 8;
    doc.setFillColor(...primaryNavy);
    doc.rect(margin, y, col1W + col2W, tableHeaderH, 'F');
    // Resaltado sutil esmeralda para la cabecera de MiA Cartera PH
    doc.setFillColor(6, 78, 59); // Dark Emerald
    doc.rect(margin + col1W + col2W, y, col3W, tableHeaderH, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(255, 255, 255);
    doc.text('VARIABLE', margin + 3.5, y + 5.2);
    doc.text('COBRO TRADICIONAL / ABOGADO EXTERNO', margin + col1W + 4, y + 5.2);
    doc.text('CON MiA CARTERA PH (LEGALTECH)', margin + col1W + col2W + 4, y + 5.2);
    y += tableHeaderH;

    const rows = [
      {
        aspect: 'Costo para el Conjunto',
        trad: '15% a 30% de honorarios sobre el capital recuperado.',
        mia: 'Tarifa plana mensual fija en pesos (desde $350.000 COP). Cero comisiones sobre el recaudo.'
      },
      {
        aspect: 'Forma y Facilidad de Pago',
        trad: 'Consignación en ventanilla bancaria, envío de recibos manuales o fotos borrosas.',
        mia: 'Botón oficial de pago PSE / Wompi directo en WhatsApp. El residente paga en 30 segundos.'
      },
      {
        aspect: 'Cumplimiento Ley 2300 (SIC)',
        trad: 'Alto riesgo de llamadas y mensajes fuera de horario hábil o fines de semana.',
        mia: 'Blindaje algorítmico 100%: bloquea despachos fuera de horario y genera bitácora inmutable.'
      },
      {
        aspect: 'Preparación para Juzgado',
        trad: 'Semanas calculando intereses manualmente y redactando certificaciones en Word.',
        mia: '1 Clic para descargar el Título Ejecutivo formal (Art. 48 Ley 675) con intereses SFC para embargo.'
      },
      {
        aspect: 'Relación y Convivencia',
        trad: 'Tensiones constantes, discusiones en portería y confrontaciones en asambleas.',
        mia: 'Comunicación institucional respetuosa con enlace a portal de autogestión de acuerdos de pago.'
      }
    ];

    rows.forEach((r, idx) => {
      const isEven = idx % 2 === 1;

      // Medir alturas dinámicamente con anchos seguros para evitar colisiones
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.2);
      const splitTrad = doc.splitTextToSize(r.trad, 61); // 61 mm de texto (margen de 4.8 mm antes del divisor)

      doc.setFont('helvetica', 'bold');
      const splitMia = doc.splitTextToSize(r.mia, 62.5); // 62.5 mm de texto (margen de 4.9 mm antes del borde)

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      const splitAspect = doc.splitTextToSize(r.aspect, col1W - 7);

      const maxLines = Math.max(splitAspect.length, splitTrad.length, splitMia.length);
      const rowH = Math.max(15, maxLines * 4.2 + 5.0);

      // Fondo columnas 1 y 2
      doc.setFillColor(isEven ? 255 : 248, isEven ? 255 : 250, isEven ? 255 : 252);
      doc.rect(margin, y, col1W + col2W, rowH, 'F');

      // Fondo destacado para Columna 3 (MiA Cartera PH)
      doc.setFillColor(236, 253, 245); // Emerald 50
      doc.rect(margin + col1W + col2W, y, col3W, rowH, 'F');

      // Bordes y cuadrícula de la tabla
      doc.setDrawColor(...cardBorder);
      doc.setLineWidth(0.3);
      doc.rect(margin, y, contentWidth, rowH, 'D');

      // Líneas divisorias verticales
      doc.line(margin + col1W, y, margin + col1W, y + rowH);
      doc.line(margin + col1W + col2W, y, margin + col1W + col2W, y + rowH);

      const textBaselineY = y + 4.8;
      const badgeCenterY = textBaselineY - 1.1;

      // Aspecto / Variable
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(...primaryNavy);
      doc.text(splitAspect, margin + 3.5, textBaselineY);

      // Tradicional (Badge circular rojo vectorizado + texto Rose-800 sobrio y nítido)
      drawWarningBadge(margin + col1W + 4.2, badgeCenterY, 1.7);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.2);
      doc.setTextColor(159, 18, 57); // Rose 800
      doc.text(splitTrad, margin + col1W + 8.2, textBaselineY);

      // MiA (Badge circular esmeralda vectorizado + texto darkEmerald en negrita)
      drawSuccessBadge(margin + col1W + col2W + 4.2, badgeCenterY, 1.7);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.2);
      doc.setTextColor(...darkEmerald);
      doc.text(splitMia, margin + col1W + col2W + 8.2, textBaselineY);

      y += rowH;
    });

    y += 4;
    const noteH = 6.5;
    doc.setFillColor(...lightEmeraldBg);
    doc.roundedRect(margin, y, contentWidth, noteH, 1.2, 1.2, 'F');
    doc.setDrawColor(...emeraldAccent);
    doc.setLineWidth(0.3);
    doc.roundedRect(margin, y, contentWidth, noteH, 1.2, 1.2, 'D');

    drawSuccessBadge(margin + 4.5, y + noteH / 2, 1.5);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.2);
    doc.setTextColor(...darkEmerald);
    doc.text(
      'Respaldo institucional bajo Ley 675 de 2001, Código General del Proceso (CGP) y Ley 527 de 1999 (Validez Probatoria Digital).',
      margin + 8.5,
      y + 4.3
    );

    // ==========================================
    // ── PÁGINA 2: LOS 6 PILARES DE LA PLATAFORMA
    // ==========================================
    doc.addPage();
    y = renderPageHeader(2, 3, 'Funcionalidades y Cumplimiento');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...primaryNavy);
    doc.text('2. Arquitectura de Cobranza & Cumplimiento Normativo', margin, y);
    y += 4;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...textDark);
    doc.text(
      'La plataforma opera como un orquestador inteligente que acompaña la cartera en cada etapa procesal, desde la prevención hasta el cobro judicial pleno bajo el Régimen de Propiedad Horizontal colombiano.',
      margin,
      y
    );
    y += 7;

    // 6 Pilares en cuadrícula (2 columnas x 3 filas) con altura generosa (43 mm)
    const pillarW = (contentWidth - 6) / 2; // 90.9 mm
    const pillarH = 43;

    const pillars = [
      {
        num: '1',
        title: 'Carga Masiva Inteligente desde Excel',
        badge: 'Compatibilidad Universal',
        desc: 'Exporte la planilla de cartera de su software contable actual (Daytona, SISCO, Siigo, WordOffice) y cárguela en 3 segundos. El motor clasifica automáticamente unidades privadas, capital, cuotas morosas y datos de contacto sin digitación manual.'
      },
      {
        num: '2',
        title: 'Notificaciones WhatsApp con Botón PSE',
        badge: 'API Oficial de Meta Business',
        desc: 'Cada propietario recibe en su celular un requerimiento formal con botón de pago directo a su entidad bancaria (PSE / Wompi). El pago ingresa de inmediato y sin intermediarios a la cuenta oficial de la copropiedad.'
      },
      {
        num: '3',
        title: 'Semáforo Legal de Mora & Etapas',
        badge: 'Ley 675 Art. 29 y 30',
        desc: 'Segmentación automatizada en 3 etapas: Preventiva y Pronto Pago (1 a 30 días), Requerimiento Prejurídico Art. 48 (31 a 60 días) y Aviso Prejudicial de Demanda y Embargo (>60 días de mora) con liquidación oficial de intereses SFC.'
      },
      {
        num: '4',
        title: 'Título Ejecutivo en PDF en 1 Clic',
        badge: 'Mérito Ejecutivo Pleno',
        desc: 'En caso de no pago, emite al instante el Certificado de Deuda formal (Ley 675 Art. 48) firmado por el administrador con intereses de mora certificados por la Superfinanciera, listo para que el abogado radique demanda civil de embargo.'
      },
      {
        num: '5',
        title: 'Portal de Acuerdos & Paz y Salvos',
        badge: 'Firma Digital Ley 527 de 1999',
        desc: 'El copropietario accede a un portal exclusivo donde puede simular planes de financiación aprobados por la administración, suscribir un Contrato de Transacción Extrajudicial formal y descargar automáticamente su Paz y Salvo al finalizar.'
      },
      {
        num: '6',
        title: 'Blindaje Ley 2300 de 2023 ("Dejen de Fregar")',
        badge: 'Seguridad Jurídica Total ante la SIC',
        desc: 'Bloquea envíos en fines de semana, festivos y fuera del horario legal (Lunes a Viernes 7am-7pm, Sábados 8am-3pm). Genera una bitácora inmutable de despachos para responder ante cualquier queja ante la SIC.'
      }
    ];

    pillars.forEach((p, idx) => {
      const col = idx % 2;
      const row = Math.floor(idx / 2);
      const pX = margin + col * (pillarW + 6);
      const pY = y + row * (pillarH + 4);

      doc.setFillColor(...lightBg);
      doc.roundedRect(pX, pY, pillarW, pillarH, 2.5, 2.5, 'FD');
      doc.setDrawColor(...cardBorder);
      doc.setLineWidth(0.4);
      doc.roundedRect(pX, pY, pillarW, pillarH, 2.5, 2.5, 'D');

      // Círculo numerado
      doc.setFillColor(...emeraldAccent);
      doc.circle(pX + 5.5, pY + 6.5, 3.5, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.text(p.num, pX + 5.5, pY + 7.8, { align: 'center' });

      // Título
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(...primaryNavy);
      doc.text(p.title, pX + 11.5, pY + 7);

      // Badge
      doc.setFontSize(6.5);
      doc.setTextColor(...darkEmerald);
      doc.text(`[ ${p.badge} ]`, pX + 11.5, pY + 11.5);

      // Descripción
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.2);
      doc.setTextColor(...textDark);
      const splitDesc = doc.splitTextToSize(p.desc, pillarW - 10);
      doc.text(splitDesc, pX + 5, pY + 16.5);
    });

    y += 3 * (pillarH + 4) + 6;

    // Sección: Flujo en 3 Pasos
    const flowBoxH = 50;
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(margin, y, contentWidth, flowBoxH, 3, 3, 'FD');
    doc.setDrawColor(...darkEmerald);
    doc.setLineWidth(0.7);
    doc.roundedRect(margin, y, contentWidth, flowBoxH, 3, 3, 'D');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(...darkEmerald);
    doc.text('FLUJO OPERATIVO SENCILLO PARA EL ADMINISTRADOR (3 PASOS)', margin + 6, y + 7.5);

    const steps = [
      {
        step: 'PASO 01',
        title: 'Exportar & Subir Excel',
        desc: 'Exporte el balance de cartera de su software contable actual y súbalo a MiA en 1 clic. El sistema clasifica los saldos al instante.'
      },
      {
        step: 'PASO 02',
        title: 'Despacho Automatizado PSE',
        desc: 'El motor verifica horarios legales y despacha notificaciones personalizadas con enlace seguro de pago a WhatsApp y Correo.'
      },
      {
        step: 'PASO 03',
        title: 'Recaudo Inmediato o Título',
        desc: 'El residente paga desde su banco en 30 segundos, o se genera el Título Ejecutivo Art. 48 para radicar demanda de embargo.'
      }
    ];

    const stepW = (contentWidth - 12) / 3;
    steps.forEach((st, idx) => {
      const sX = margin + 6 + idx * stepW;
      const sY = y + 12;
      const sH = flowBoxH - 17;

      doc.setFillColor(...lightBg);
      doc.roundedRect(sX, sY, stepW - 3, sH, 2, 2, 'FD');
      doc.setDrawColor(...cardBorder);
      doc.roundedRect(sX, sY, stepW - 3, sH, 2, 2, 'D');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(...emeraldAccent);
      doc.text(st.step, sX + 4, sY + 6);

      doc.setFontSize(8.5);
      doc.setTextColor(...primaryNavy);
      doc.text(st.title, sX + 4, sY + 11.5);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...textDark);
      doc.text(doc.splitTextToSize(st.desc, stepW - 10), sX + 4, sY + 16.5);
    });

    // ==========================================
    // ── PÁGINA 3: PLANES, RETORNO (ROI) & CONTACTO
    // ==========================================
    doc.addPage();
    y = renderPageHeader(3, 3, 'Propuesta Económica & Planes');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...primaryNavy);
    doc.text('3. Planes de Suscripción Mensual por Copropiedad', margin, y);
    y += 4;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...textDark);
    doc.text(
      'Tarifas fijas transparentes en pesos colombianos (COP). Sin cobro de comisiones ni porcentajes sobre el capital que recupere la copropiedad.',
      margin,
      y
    );
    y += 7;

    // Tabla de Planes Oficiales
    const planW = (contentWidth - 6) / 3;
    const planH = 106; // Altura optimizada para dar respiro a todas las características
    const planCards = [
      {
        name: 'COPROPIEDAD PEQUEÑA',
        units: 'Hasta 80 Unidades Privadas',
        price: '$350.000',
        period: 'COP / mes por copropiedad',
        isBest: false,
        waQuota: '250 WhatsApp PSE / mes',
        mailQuota: '500 Correos / mes',
        features: [
          'Carga masiva desde Excel',
          'Semáforo de mora Ley 675',
          'Título Ejecutivo Art. 48 en PDF',
          'Blindaje y bitácora Ley 2300',
          'Soporte técnico por correo'
        ]
      },
      {
        name: 'COPROPIEDAD MEDIANA',
        units: '81 a 200 Unidades Privadas',
        price: '$650.000',
        period: 'COP / mes por copropiedad',
        isBest: true,
        waQuota: '600 WhatsApp PSE / mes',
        mailQuota: '1.200 Correos / mes',
        features: [
          'Todo lo del Plan Pequeño',
          'Portal de Acuerdos de Pago',
          'Generación de Paz y Salvos',
          'Soporte prioritario para el Admin',
          'Informe ejecutivo para el Consejo',
          'Campañas quincenales automáticas'
        ]
      },
      {
        name: 'COPROPIEDAD GRANDE',
        units: 'Más de 200 Unidades Privadas',
        price: '$1.150.000',
        period: 'COP / mes por copropiedad',
        isBest: false,
        waQuota: '1.500 WhatsApp PSE / mes',
        mailQuota: '3.000 Correos / mes',
        features: [
          'Todo lo del Plan Mediano',
          'Personalización con logo del PH',
          'Títulos ejecutivos masivos',
          'Capacitación en vivo para Consejo',
          'Asesoría procesal en Ley 675',
          'Gestor de cuenta corporativo'
        ]
      }
    ];

    planCards.forEach((pc, idx) => {
      const pX = margin + idx * (planW + 3);

      doc.setFillColor(pc.isBest ? 255 : 248, pc.isBest ? 255 : 250, pc.isBest ? 255 : 252);
      doc.roundedRect(pX, y, planW, planH, 2.5, 2.5, 'FD');
      doc.setDrawColor(...(pc.isBest ? emeraldAccent : cardBorder));
      doc.setLineWidth(pc.isBest ? 0.8 : 0.3);
      doc.roundedRect(pX, y, planW, planH, 2.5, 2.5, 'D');

      let innerY = y + 4.5;

      if (pc.isBest) {
        doc.setFillColor(...emeraldAccent);
        doc.roundedRect(pX + planW / 2 - 20, innerY - 2, 40, 4.8, 1, 1, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6.5);
        doc.setTextColor(255, 255, 255);
        doc.text('MÁS RECOMENDADO', pX + planW / 2, innerY + 1.2, { align: 'center' });
        innerY += 5.5;
      } else {
        innerY += 2;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(...(pc.isBest ? darkEmerald : primaryNavy));
      doc.text(pc.name, pX + planW / 2, innerY, { align: 'center' });
      innerY += 4.2;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...textMuted);
      doc.text(pc.units, pX + planW / 2, innerY, { align: 'center' });
      innerY += 5.8;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(...primaryNavy);
      doc.text(pc.price, pX + planW / 2, innerY, { align: 'center' });
      innerY += 4.5;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.8);
      doc.setTextColor(...darkEmerald);
      doc.text(pc.period, pX + planW / 2, innerY, { align: 'center' });
      innerY += 4.8;

      // Caja de cupos
      doc.setFillColor(...(pc.isBest ? lightEmeraldBg : lightGrayBg));
      doc.roundedRect(pX + 3.5, innerY, planW - 7, 10.5, 1.5, 1.5, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.8);
      doc.setTextColor(...darkEmerald);
      doc.text(pc.waQuota, pX + planW / 2, innerY + 3.8, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.4);
      doc.setTextColor(...textDark);
      doc.text(pc.mailQuota, pX + planW / 2, innerY + 7.6, { align: 'center' });

      innerY += 13.0;

      doc.setDrawColor(...cardBorder);
      doc.setLineWidth(0.3);
      doc.line(pX + 4, innerY, pX + planW - 4, innerY);
      innerY += 4.0;

      pc.features.forEach((feat) => {
        // Bullet circular verde con checkmark blanco nítido (evita caracteres o comillas extrañas)
        drawPlanBullet(pX + 5.2, innerY - 0.9, pc.isBest);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.8);
        doc.setTextColor(...textDark);
        // Margen derecho seguro para evitar cortes de texto en bordes
        const splitFeat = doc.splitTextToSize(feat, planW - 13);
        doc.text(splitFeat, pX + 8.5, innerY);
        innerY += splitFeat.length * 3.3 + 1.2;
      });
    });

    y += planH + 5;

    // Escalas de Descuento Multicopropiedad (Banner especial para Administradores de varias PHs)
    const discBoxH = 25;
    doc.setFillColor(...lightBg);
    doc.roundedRect(margin, y, contentWidth, discBoxH, 2.5, 2.5, 'FD');
    doc.setDrawColor(...cardBorder);
    doc.roundedRect(margin, y, contentWidth, discBoxH, 2.5, 2.5, 'D');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...primaryNavy);
    doc.text('¿Administra varias copropiedades a la vez o es Empresa de Administración?', margin + 6, y + 5.5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...textDark);
    doc.text('Tarifas preferenciales por volumen para administradores profesionales con panel centralizado multi-PH:', margin + 6, y + 9.5);

    const discounts = [
      { range: '2 a 3 Copropiedades', desc: '15% de Descuento por PH' },
      { range: '4 a 6 Copropiedades', desc: '25% de Descuento por PH' },
      { range: '7 o más Copropiedades', desc: '35% de Descuento por PH' }
    ];

    const discCardW = (contentWidth - 18) / 3;
    discounts.forEach((d, idx) => {
      const dX = margin + 6 + idx * (discCardW + 3);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(dX, y + 12.5, discCardW, 9.5, 1.5, 1.5, 'FD');
      doc.setDrawColor(...darkEmerald);
      doc.setLineWidth(0.4);
      doc.roundedRect(dX, y + 12.5, discCardW, 9.5, 1.5, 1.5, 'D');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...primaryNavy);
      doc.text(d.range, dX + discCardW / 2, y + 16.5, { align: 'center' });

      doc.setFontSize(6.5);
      doc.setTextColor(...darkEmerald);
      doc.text(d.desc, dX + discCardW / 2, y + 20, { align: 'center' });
    });

    y += discBoxH + 5;

    // Retorno de Inversión (ROI) y Contacto Comercial
    const roiBoxH = 48;
    doc.setFillColor(...primaryNavy);
    doc.roundedRect(margin, y, contentWidth, roiBoxH, 3, 3, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(255, 255, 255);
    doc.text('CÓMO SE PAGA SOLO: RETORNO DE INVERSIÓN COMPROBADO (ROI)', margin + 7, y + 7.5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.8);
    doc.setTextColor(226, 232, 240);
    const roiExplanation =
      'En un conjunto de 120 apartamentos con cuota de $350.000 COP, recuperar solo 2 cuotas morosas en el mes ($700.000 COP) cubre con creces el costo del Plan Mediano ($650.000 COP). Al ingresar entre 8 y 15 cuotas adicionales gracias al botón PSE en WhatsApp, la copropiedad obtiene un retorno de inversión real superior a 9x su inversión mensual.';
    doc.text(doc.splitTextToSize(roiExplanation, contentWidth - 14), margin + 7, y + 13);

    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.2);
    doc.line(margin + 7, y + 26, margin + contentWidth - 7, y + 26);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(52, 211, 153); // Emerald 400
    doc.text('SOLICITE UNA DEMOSTRACIÓN DE 15 MINUTOS CON SU ARCHIVO REAL DE CARTERA', margin + 7, y + 33);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text('WhatsApp: +57 350 767 9981   |   Email: contacto@miasistentelegalia.com   |   Web: www.miasistentelegalia.com', margin + 7, y + 41);

    return doc;
  }
}
